/**
 * FEEDBACK, at `/feedback/:visitId`. Built from `faithfully-kept-business/design/home/
 * Feedback.dc.html`, its structure and its words, with the visit ops pushed in place of the sample
 * one, as GET /api/visits/:id answers it. The words are in `words.ts`.
 *
 * - THE ARTBOARD, IN ORDER: the visit's day and "How was it?", with the X back to Home; the four
 *   faces; which area, as chips; one line of what was missed; the 24-hour line; and Send. With
 *   several homes, which no artboard draws, the visit's home is named under "How was it?" by its
 *   label alone (`homeNameOf`, provisional P40), so feedback on one of two cleans on a day is never
 *   read as the other's; with one home nothing is named, as drawn.
 * - VERY GOOD ASKS NOTHING MORE. With "Very good" chosen the page is the faces and Send alone: which
 *   area, what was missed and the 24-hour line read oddly after the top face, so they fold away, and
 *   the request is the face and the visit, with no area and no line, whatever was chosen or typed
 *   under another face. Choosing another face draws them again as she left them. The other three
 *   faces, and no face yet, draw the artboard whole. This is home's own small call on how the page
 *   reads, within the rules: the artboard draws Mixed, and the seam lets a face go alone. Which face
 *   asks what is one rule with an invoice's "How did we do?" (`lib/faces.ts`, slice V4), since the
 *   owner decided that any answer naming a missed area starts a re-clean, Good included (ops #72).
 * - REACHED FROM HOME. A face tapped on the card of a last clean, of which Home draws one for each
 *   home's last clean, comes here for that clean with that face chosen (`feedbackPath`); a face
 *   word this build does not know chooses none, and Send waits for one.
 * - WHAT IS SENT is a `visit.feedback` request (the seam, section 4): the face, as the seam names
 *   it; the area of a named chip, and "Other" for her line under the Other chip or with no chip
 *   chosen, since a line alone names no area and an area named starts the re-clean (`areaSent`,
 *   ops's section 4 at 9f10a8b and section 11.6, question 7, round four's slice F1), and none for
 *   the Other chip with nothing written, as the owner accepted on 2026-09-28; and the line exactly
 *   as she typed it, up to the seam's 280 characters, left out when blank. What she typed travels in
 *   the body of the request, never in a path or a query.
 * - THE 24-HOUR LINE IS DRAWN WHILE IT HOLDS, for every face but Very good: on the clean's own day
 *   and the day after, in Florida's days, the place's `today` and the visit's day
 *   (`recleanPromised`, slice F1), as the artboard draws it; after that, and for a visit with no day,
 *   the page reads without it. It is the business's own sentence, word for word.
 * - A TAP ON A FACE THAT ASKS MORE brings "Which area?" down to Send into view, clear of the nav,
 *   smoothly unless she asked the device for less motion (`bringIntoView`, slice F1); arriving with
 *   a face chosen moves nothing.
 * - A CHOSEN FACE IS NOT A SENT ONE (round six, slice S3, P86). On this page every face waits for
 *   Send and none is drawn once sent, so a chosen face is drawn as an invoice draws one tapped: a
 *   ring and no fill (`FACE_CHOSEN`, `components/faceLooks.ts`), described to a screen reader as
 *   "Not sent until you press Send." (`INVOICE_FEEDBACK.notSent`). The artboard fills it; the replay
 *   of the owner's household on 2026-10-18 found a face tapped on Home arriving here in the navy
 *   fill Home draws a sent answer in, so she could leave by the X or the nav thinking it had gone.
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). Once the door has written the request the
 *   page says it was sent to the office, and nothing about a task, an issue or a clean again: those
 *   are ops's to open, and the office's to say. After an answer that named what was missed (an area
 *   sent), one line more says what happens next, "The office will be in touch to put it right."
 *   (`FEEDBACK.inTouch`, round six, slice S3), read out with "Sent to the office." from where the
 *   focus goes; it never says a clean is booked.
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED. The key is minted once for the answer it was minted
 *   for, so a retry after a failure names the same request and the door writes one row, and a
 *   changed answer is a new key. While one is out, another tap does nothing.
 * - A CODE STAYS OUT. The door runs the words test before it stores anything, and a line that looks
 *   like a code is refused there: the page says the seam's words, marks the field and gives it the
 *   focus, and keeps what she wrote so she can take the code out.
 * - WHAT THE DOOR ANSWERS decides what follows: sent is said here; a 401 is the way to the door; a
 *   404 reads the visit again; `403 not_asked` says the office did not ask her about the clean,
 *   with the phone (home #23); anything else says it did not go through, with the phone, and keeps
 *   her answer to try again.
 * - ONLY A VISIT SHE MAY GIVE FEEDBACK ON, as the Worker decides. A visit that does not offer
 *   `visit.feedback`, as for a person ops did not ask about the clean (home #23), and one the Worker
 *   does not find for her, which for a member is any visit at a home that is not hers, go back to
 *   Home, which draws the clean without its faces, and whose own read then says where she stands:
 *   the page that says to call, when ops has revoked her. A 401 is the door.
 * - CHOICES ARE RADIO BUTTONS: one face and one area, each group named by its question, so the
 *   arrow keys move within a group and a screen reader says which is chosen. The drawn circle or
 *   chip carries the focus ring for the button it stands for.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { getVisit, sendRequest, type PlaceAnswer, type TooMany, type VisitPlace } from '../api.ts';
import { NAVY_BUTTON } from '../components/buttons.ts';
import { FACE_CHOSEN, FACE_FREE } from '../components/faceLooks.ts';
import { FaceIcon } from '../components/FaceIcon.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Shell } from '../components/Shell.tsx';
import { dayInWords } from '../lib/dates.ts';
import { areaSent, asksWhatWasMissed, recleanPromised } from '../lib/faces.ts';
import { bringIntoView, CLEAR_OF_THE_NAV } from '../lib/motion.ts';
import { homeNameOf } from '../lib/visits.ts';
import { HOME_PATH } from '../paths.ts';
import {
  AREAS,
  CLOSE,
  FACES,
  FAILED_BEFORE_PHONE,
  FEEDBACK,
  HOME,
  INVOICE_FEEDBACK,
  LOOKS_LIKE_A_CODE,
  NOT_ASKED_BEFORE_PHONE,
  REQUEST,
  tooManyBeforePhone,
  type Face,
} from '../words.ts';

/** The seam's limit on a feedback line. */
export const FEEDBACK_LINE_MAX = 280;

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';
const QUESTION = 'text-body font-semibold text-text-body';

/** The focus ring, drawn on a choice's circle or chip while its hidden radio button has it. */
const RING =
  'has-focus-visible:outline-3 has-focus-visible:outline-offset-2 has-focus-visible:outline-focus-ring';

const CHIP =
  'flex min-h-tap cursor-pointer items-center gap-1.5 rounded-control border-2 px-4 text-small';
const CHIP_CHOSEN = 'border-surface-brand bg-surface-brand-soft font-semibold text-text-body';
const CHIP_FREE =
  'border-border-strong bg-surface-raised text-text-body hover:bg-surface-accent-soft';

/** The visit, or null when the Worker does not find it for her, which goes back to Home. */
async function visitOrNone(id: string): Promise<PlaceAnswer<VisitPlace | null>> {
  const answer = await getVisit(id);
  return answer.outcome === 'no_home' ? { outcome: 'shown', place: null } : answer;
}

export function Feedback(signed: Signed) {
  const { visitId = '' } = useParams();
  const load = useCallback(() => visitOrNone(visitId), [visitId]);
  return (
    // Keyed by the visit, so another visit's page is another read.
    <Guarded key={visitId} load={load} signed={signed} at="home" heading={HOME.howWasIt}>
      {(place, reload) =>
        place === null || !place.visit.offers.includes('visit.feedback') ? (
          <Navigate to={HOME_PATH} replace />
        ) : (
          <FeedbackDrawn place={place} signed={signed} reload={reload} />
        )
      }
    </Guarded>
  );
}

/** A face named in the query, or none when the word is not one of the four. */
const faceNamed = (word: string | null): Face | null =>
  FACES.find(({ face }) => face === word)?.face ?? null;

/**
 * What the page says after an answer that was not sent: a code in the line; ops already holds its
 * twelve answers from home on the clean (`409 answers_used_up`); ops did not ask her about it
 * (`403 not_asked`, home #23); past the door's own limits; or a failure.
 */
type Problem = 'code' | 'answered' | 'not_asked' | 'too_many' | 'failed';

/**
 * What each problem with the phone after it says before the phone, past the door's own limits with
 * whose count and when, as the door said (round six, slice S1).
 */
function beforePhone(problem: Exclude<Problem, 'code'>, limit: TooMany | undefined): string {
  switch (problem) {
    case 'answered':
      return HOME.alreadyBeforePhone;
    case 'not_asked':
      return NOT_ASKED_BEFORE_PHONE;
    case 'too_many':
      return tooManyBeforePhone(limit);
    default:
      return FAILED_BEFORE_PHONE;
  }
}

/** What a refusal of the door's says: its twelve answers held, not asked, or that it failed. */
const refusedAs = (code: string | undefined): Problem =>
  code === 'answers_used_up' ? 'answered' : code === 'not_asked' ? 'not_asked' : 'failed';

/**
 * Whether the page asks which area and what was missed, with the 24-hour line: for no face yet, as
 * the artboard draws it, and for every face that asks it by the rule an invoice's "How did we do?"
 * asks it by (`asksWhatWasMissed`, ops #72). Very good asks nothing more.
 */
const asksMore = (face: Face | null) => face === null || asksWhatWasMissed(face);

/** Feedback's drawn X. */
function Cross() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 5l10 10M15 5 5 15" />
    </svg>
  );
}

/** The check drawn on the chosen area. */
function Check() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.5 10.5 8.5 14.5 15.5 6" />
    </svg>
  );
}

interface DrawnProps {
  readonly place: VisitPlace;
  readonly signed: Signed;
  readonly reload: () => void;
}

function FeedbackDrawn({ place, signed, reload }: DrawnProps) {
  const [params] = useSearchParams();
  const headingId = useId();
  const areaId = useId();
  const lineId = useId();
  const problemId = useId();
  const faceGroup = useId();
  const areaGroup = useId();
  const notSentId = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const sentLine = useRef<HTMLDivElement>(null);
  const asking = useRef<HTMLDivElement>(null);
  const [face, setFace] = useState<Face | null>(() => faceNamed(params.get('face')));
  /** Counts her taps on a face that asks more: each brings the question into view. */
  const [asked, setAsked] = useState(0);
  /** The label of the area chosen. */
  const [chip, setChip] = useState<string | null>(null);
  const [line, setLine] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  /** Whose count of the door's own limits it was past, and when to try again, as the door said. */
  const [limit, setLimit] = useState<TooMany>();
  const [sent, setSent] = useState(false);
  /** The answer sent named what was missed, so the office will be in touch (round six, slice S3). */
  const [named, setNamed] = useState(false);
  /** A request is out, so another tap does nothing. */
  const out = useRef(false);
  const mounted = useRef(true);
  /** The key minted for the answer it was minted for. */
  const minted = useRef<{ readonly answer: string; readonly key: string } | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (sent) sentLine.current?.focus();
  }, [sent]);

  useEffect(() => {
    if (asked > 0) bringIntoView(asking.current);
  }, [asked]);

  const { visit } = place;
  const day = visit.date === undefined ? null : dayInWords(visit.date);
  const home = homeNameOf(visit, place.homes);
  const more = asksMore(face);
  /** A line refused as a code says so only while the line is drawn, since only then does it go. */
  const said = problem === 'code' && !more ? null : problem;

  function chose(each: Face) {
    setFace(each);
    if (asksWhatWasMissed(each)) setAsked((count) => count + 1);
  }

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (out.current || face === null) return;
    out.current = true;
    setProblem(null);
    // The area and the line go only with a face that asks them; Very good goes alone. A named
    // chip, else "Other" for her line, under Other or alone (`areaSent`, ops #72).
    const area = more ? areaSent(chip, line) : null;
    const payload = {
      visit_id: visit.id,
      face,
      ...(area === null ? {} : { area }),
      ...(!more || line.trim() === '' ? {} : { line }),
    };
    const answer = JSON.stringify(payload);
    if (minted.current?.answer !== answer) minted.current = { answer, key: crypto.randomUUID() };
    const outcome = await sendRequest({
      key: minted.current.key,
      type: 'visit.feedback',
      household: place.household.id,
      payload,
    });
    out.current = false;
    if (!mounted.current) return;
    switch (outcome.outcome) {
      case 'sent':
        setNamed(area !== null);
        setSent(true);
        return;
      case 'signed_out':
        signed.onSignedOut();
        return;
      case 'not_found':
        setProblem('failed');
        reload();
        return;
      case 'looks_like_a_code':
        setProblem('code');
        field.current?.focus();
        return;
      case 'too_many':
        setLimit(outcome.limit);
        setProblem('too_many');
        return;
      case 'refused':
        setProblem(refusedAs(outcome.code));
        return;
      default:
        setProblem('failed');
    }
  }

  return (
    <Shell at="home" payments={place.household.payments}>
      <div className="flex items-start justify-between gap-3">
        <div>
          {day ? <p className={EYEBROW}>{day}</p> : null}
          <h1
            id={headingId}
            className="mt-1 font-serif text-h2 font-semibold text-pretty text-text-body"
          >
            {HOME.howWasIt}
          </h1>
          {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
        </div>
        <Link
          to={HOME_PATH}
          aria-label={CLOSE}
          className="flex size-12 shrink-0 items-center justify-center rounded-control border-2 border-border-strong bg-surface-raised text-text-brand hover:bg-surface-accent-soft"
        >
          <Cross />
        </Link>
      </div>

      {sent ? (
        <div ref={sentLine} tabIndex={-1} className="flex flex-col gap-2">
          <p className="text-body text-pretty">{REQUEST.sent}</p>
          {named ? <p className="text-body text-pretty">{FEEDBACK.inTouch}</p> : null}
        </div>
      ) : (
        <form method="post" onSubmit={(event) => void send(event)} className="-mt-1 flex flex-col">
          <div role="radiogroup" aria-labelledby={headingId} className="grid grid-cols-4 gap-1">
            {FACES.map(({ face: each, label }) => {
              const on = face === each;
              return (
                <label
                  key={each}
                  className={`flex cursor-pointer flex-col items-center gap-1 rounded-control py-1 ${RING}`}
                >
                  <input
                    type="radio"
                    name={faceGroup}
                    value={each}
                    checked={on}
                    onChange={() => chose(each)}
                    aria-describedby={on ? notSentId : undefined}
                    className="sr-only"
                  />
                  <span
                    className={`flex size-15 items-center justify-center rounded-full border-2 ${
                      on ? FACE_CHOSEN : FACE_FREE
                    }`}
                  >
                    <FaceIcon face={each} />
                  </span>
                  <span
                    className={`text-center text-small whitespace-nowrap text-text-body ${on ? 'font-semibold' : ''}`}
                  >
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
          {face === null ? null : (
            <span id={notSentId} className="sr-only">
              {INVOICE_FEEDBACK.notSent}
            </span>
          )}

          <div ref={asking} className={`flex flex-col ${CLEAR_OF_THE_NAV}`}>
            {more ? (
              <>
                <p id={areaId} className={`mt-5 ${QUESTION}`}>
                  {FEEDBACK.whichArea}
                </p>
                <div
                  role="radiogroup"
                  aria-labelledby={areaId}
                  className="mt-2.5 flex flex-wrap gap-2"
                >
                  {AREAS.map(({ label }) => {
                    const on = chip === label;
                    return (
                      <label
                        key={label}
                        className={`${CHIP} ${on ? CHIP_CHOSEN : CHIP_FREE} ${RING}`}
                      >
                        <input
                          type="radio"
                          name={areaGroup}
                          value={label}
                          checked={on}
                          onChange={() => setChip(label)}
                          className="sr-only"
                        />
                        {on ? <Check /> : null}
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>

                <label htmlFor={lineId} className={`mt-5 ${QUESTION}`}>
                  {FEEDBACK.whatWasMissed}
                </label>
                <textarea
                  ref={field}
                  id={lineId}
                  value={line}
                  onChange={(event) => setLine(event.target.value)}
                  rows={1}
                  maxLength={FEEDBACK_LINE_MAX}
                  aria-invalid={said === 'code' ? true : undefined}
                  aria-describedby={said === null ? undefined : problemId}
                  className="mt-2 field-sizing-content max-h-48 min-h-tap-call resize-none rounded-control border-2 border-border-strong bg-surface-raised px-4 py-3 text-body text-text-body"
                />

                {recleanPromised(visit.date, place.today) ? (
                  <p className="mt-4 rounded-card border border-border-subtle bg-surface-brand-soft px-4 py-3.5 text-small text-pretty text-text-body">
                    {FEEDBACK.noCharge}
                  </p>
                ) : null}
              </>
            ) : null}

            <div aria-live="polite" id={problemId}>
              {said === null ? null : (
                <p className="mt-4 text-body text-pretty">
                  {said === 'code' ? (
                    LOOKS_LIKE_A_CODE
                  ) : (
                    <>
                      {beforePhone(said, limit)} <Phone after="." />
                    </>
                  )}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={face === null}
              className={`mt-4 ${NAVY_BUTTON} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {FEEDBACK.send}
            </button>
          </div>
        </form>
      )}
    </Shell>
  );
}
